import { formatStreamingChunk } from './formatStreamingChunk.js';
import { formatResponse } from './formatResponse.js';
import { expandQuery } from './expandQuery.js';
import { splitRelatedQuestions } from './splitRelatedQuestions.js';
import { PREDEFINED_QUESTIONS } from '../data/predefined-questions.js';

document.addEventListener('DOMContentLoaded', () => {
  const searchButton = document.getElementById('search-button');
  const searchInput = document.getElementById('search-input');
  const resultContainer = document.getElementById('result');
  const errorContainer = document.getElementById('error');
  const imageContainer = document.getElementById('image-container');
  let abortController = null;
  let summaryAbortController = null;
  let history = []; // keep conversation turns here

  function scrollInputIntoView() {
    const input = document.getElementById('search-input');
    if (input) {
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
      input.focus();
    }
  }

  function clearError() {
    errorContainer.textContent = '';
  }

  function displayError(message) {
    errorContainer.textContent = `Error: ${message}`;
    resultContainer.innerHTML = '';
  }

  function showLoading() {
    resultContainer.innerHTML = '<div class="loading-animation">Loading...</div>';
  }

  function hideLoading() {
    const loading = resultContainer.querySelector('.loading-animation');
    if (loading) loading.remove();
  }

  // Circular countdown timer functions
  let countdownInterval;
  const FULL_DASH_ARRAY = 283;

  function showLoadingTime(timeLimit = 10) {
    const timer = document.getElementById('circular-countdown');
    if (!timer) {
      console.warn('Timer element not found, skipping');
      return;
    }

    timer.style.display = 'block';
    let timePassed = 0;
    let timeLeft = timeLimit;

    const label = document.getElementById('base-timer-label');
    if (label) label.textContent = timeLeft;

    setCircleDasharray(timeLeft, timeLimit);

    countdownInterval = setInterval(() => {
      timePassed += 1;
      timeLeft = timeLimit - timePassed;
      if (label) label.textContent = timeLeft > 0 ? timeLeft : '…';
      setCircleDasharray(timeLeft, timeLimit);
      if (timeLeft <= 0) {
        clearInterval(countdownInterval);
      }
    }, 1000);
  }

  function hideLoadingTime() {
    const timer = document.getElementById('circular-countdown');
    if (timer) timer.style.display = 'none';
    if (countdownInterval) clearInterval(countdownInterval);
  }

  function setCircleDasharray(timeLeft, timeLimit) {
    const path = document.getElementById('base-timer-path-remaining');
    if (path) {
      const circleDasharray = `${((timeLeft / timeLimit) * FULL_DASH_ARRAY).toFixed(0)} 283`;
      path.setAttribute('stroke-dasharray', circleDasharray);
    }
  }

  // Answers for the predefined questions are generated at build time by
  // scripts/build-precomputed-answers.mjs, because a live answer costs ~28s of
  // which ~22s is the model writing - and those questions are known in advance.
  // Fetched once on load so a click is instant. Any failure (no cache file, bad
  // JSON, offline) resolves to null and every question goes to the live API.
  let precomputedPromise = null;
  function loadPrecomputedAnswers() {
    if (!precomputedPromise) {
      precomputedPromise = fetch('/precomputed-answers.json')
        .then(response => (response.ok ? response.json() : null))
        .then(data => {
          if (data) {
            console.log(
              '📦 Precomputed answers loaded:',
              Object.keys(data.answers || {}).length,
              '| generated', data.generated_at,
              '| config', JSON.stringify(data.config)
            );
          } else {
            // Either PPLX_PRECOMPUTE is off or the build could not generate.
            console.log('📦 No precomputed answers - every question goes to the live API');
          }
          return data;
        })
        .catch(() => null);
    }
    return precomputedPromise;
  }

  async function getPrecomputedAnswer(query) {
    const data = await loadPrecomputedAnswers();
    return data?.answers?.[query] || null;
  }

  // Warm the cache as soon as the page opens.
  loadPrecomputedAnswers();

  // And warm the function itself, so a first free-text question does not pay
  // the cold start plus corpus parse. Worth well under a second, and only for
  // free-text: the predefined questions are served from the cache above and
  // never reach the function. Fire-and-forget - a failure changes nothing.
  fetch('/.netlify/functions/perplexity-api', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ warm: true }),
  }).catch(() => {});

  // Render the clickable questions from src/data/predefined-questions.js - the
  // single source of truth the build also precomputes answers from. Clicks are
  // picked up by the delegated listener further down, so rows added here need
  // no wiring of their own.
  const predefinedTable = document.getElementById('predefined-questions');
  if (predefinedTable) {
    predefinedTable.innerHTML = PREDEFINED_QUESTIONS.map(
      question =>
        `<tr><td><a href="#" class="predefined-question">${escapeHtml(question)}</a></td></tr>`
    ).join('');
  }

  // Pull the answer text out of an Agent API `output[]` array.
  function textFromOutput(output) {
    return output
      .filter(item => item?.type === 'message')
      .flatMap(item => item.content || [])
      .filter(part => part?.type === 'output_text')
      .map(part => part.text)
      .join('');
  }

  // Perplexity Agent API call with streaming support
  async function callPerplexityAPI(query, signal, onChunk = null) {
    try {
      console.log('🔵 Calling API with query:', query);
      const response = await fetch('/.netlify/functions/perplexity-api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, history, relatedQuestions: true }),
        signal,
      });
      console.log('🟢 Response status:', response.status);
      console.log('🟢 Response body:', response.body);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔴 Error response:', errorText);
        throw new Error(`HTTP error ${response.status}: ${errorText}`);
      }

      // Streaming SSE response: the Agent API emits typed events, one JSON
      // object per `data:` line. There is no [DONE] sentinel — the stream ends
      // with exactly one terminal event.
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let buffer = '';
      let citations = [];
      let streamError = null;
      let sawTerminal = false;
      let matchedSSE = false;
      let rawBody = '';
      let hitTokenCap = false;
      // Diagnostics: when each kind of event first arrived, and how long the
      // first answer token took. A run killed by the 30s function limit shows
      // only lifecycle events and never reaches response.output_text.delta;
      // the timings show which leg consumed the budget.
      const eventFirstSeen = new Map();
      const startedAt = Date.now();
      let firstDeltaMs = null;
      // A preset bundles a model rather than naming one, and may resolve to a
      // third-party model. The terminal event echoes whichever model actually
      // served the run, which is the only way to see what a preset resolves to.
      let servedModel = null;

      // Citations now live in the `search_results` output item, not at the
      // top level. Multi-step runs can emit more than one, so dedupe by URL.
      const collectSources = item => {
        if (item?.type !== 'search_results') return;
        for (const result of item.results || []) {
          if (result?.url && !citations.includes(result.url)) {
            citations.push(result.url);
          }
        }
      };

      const handleEvent = event => {
        // Error frame emitted by our own function when the upstream read fails.
        if (!event.type && event.error) {
          streamError = event.message || event.error;
          sawTerminal = true;
          return;
        }

        if (event.type && !eventFirstSeen.has(event.type)) {
          eventFirstSeen.set(event.type, Date.now() - startedAt);
        }

        switch (event.type) {
          case 'response.output_text.delta':
            if (event.delta) {
              if (firstDeltaMs === null) firstDeltaMs = Date.now() - startedAt;
              fullContent += event.delta;
              if (onChunk) onChunk(splitRelatedQuestions(fullContent).content);
            }
            break;

          case 'response.output_item.done':
            collectSources(event.item);
            break;

          // Terminal events - exactly one of these arrives.
          case 'response.completed':
          case 'response.incomplete': {
            sawTerminal = true;
            if (event.response?.model) servedModel = event.response.model;
            const output = event.response?.output || [];
            output.forEach(collectSources);
            if (!fullContent) fullContent = textFromOutput(output);
            if (event.response?.incomplete_details?.reason === 'max_output_tokens') {
              console.warn('⚠️ Answer truncated: max_output_tokens reached');
              hitTokenCap = true;
            }
            break;
          }

          case 'response.failed':
          case 'response.cancelled': {
            sawTerminal = true;
            // A failure with no message (e.g. an account-level rejection) is
            // otherwise undiagnosable, so log the whole event and surface any
            // code/type it carries rather than a bare "Run failed".
            const { instructions, tools, ...failed } = event.response || {};
            console.error(`🔴 ${event.type} event:`, { ...event, response: failed });
            const err = event.response?.error || event.error || {};
            const detail = err.message || err.code || err.type;
            streamError = detail
              ? `Run ${event.type.slice(9)}: ${detail}`
              : `Run ${event.type.slice(9)} (no reason given by Perplexity)`;
            break;
          }

          case 'error':
            sawTerminal = true;
            streamError = event.error?.message || event.message || 'Stream error';
            break;

          default:
            // Other lifecycle events (created, in_progress, reasoning, …) are
            // ignorable; unknown types are expected by design.
            break;
        }
      };

      // Accept `data:` with or without the conventional space after the colon.
      const handleLine = line => {
        if (!line.startsWith('data:')) return;
        const data = line.slice(5).trim();
        if (!data) return;
        matchedSSE = true;
        try {
          handleEvent(JSON.parse(data));
        } catch (e) {
          // Skip malformed JSON - this is normal for streaming responses
          console.log('Skipping malformed JSON line (normal for streaming)');
        }
      };

      // A non-streamed run comes back as a single JSON Response object rather
      // than an event stream. Recover the answer from it instead of showing the
      // user an empty result. Returns false if the body is not that shape.
      const recoverFromResponseObject = raw => {
        const trimmed = raw.trim();
        if (!trimmed.startsWith('{')) return false;

        let payload;
        try {
          payload = JSON.parse(trimmed);
        } catch (e) {
          return false;
        }

        // Bare error envelope: {"error": {...}}
        if (payload.error && !payload.output) {
          streamError = payload.error.message || payload.error;
          return true;
        }
        if (!payload.output) return false;

        if (payload.model) servedModel = payload.model;
        payload.output.forEach(collectSources);
        fullContent = textFromOutput(payload.output);

        // Failed and cancelled runs arrive over HTTP 200 here too.
        if (payload.status === 'failed' || payload.status === 'cancelled') {
          streamError = payload.error?.message || `Run ${payload.status}`;
        }
        if (payload.incomplete_details?.reason === 'max_output_tokens') {
          console.warn('⚠️ Answer truncated: max_output_tokens reached');
          hitTokenCap = true;
        }
        return true;
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        rawBody += chunk;
        buffer += chunk;

        // Process complete lines ending with \n
        const lines = buffer.split('\n');
        buffer = lines.pop(); // Keep the incomplete line in buffer

        for (const line of lines) handleLine(line);
      }

      // Process any remaining data in buffer
      handleLine(buffer);

      // Nothing looked like an SSE event - fall back to the single-object shape.
      if (!matchedSSE) {
        const recovered = recoverFromResponseObject(rawBody);
        console.warn(
          recovered
            ? '⚠️ Response was not an event stream; recovered from a single JSON response object'
            : '⚠️ Unrecognised response body (first 500 chars): ' + rawBody.slice(0, 500)
        );
      }

      // Debug logging
      console.log('🔍 Streaming complete. Content length:', fullContent.length);
      console.log('🔍 Sources collected:', citations.length);
      console.log('🔍 Terminal event seen:', sawTerminal);
      console.log(
        '🔍 Event types seen:',
        [...eventFirstSeen].map(([type, ms]) => `${type}@${ms}ms`).join(', ') || 'none'
      );
      console.log('🔍 Served model (what the preset resolved to):', servedModel || 'not reported');
      console.log(
        '🔍 Time to first answer token:',
        firstDeltaMs === null ? 'never arrived' : `${firstDeltaMs}ms`,
        `| total ${Date.now() - startedAt}ms`
      );

      // Failed and cancelled runs arrive over HTTP 200, so the terminal event
      // is the only signal that something went wrong.
      if (streamError) throw new Error(streamError);
      // No terminal event but text already streamed means the answer was cut
      // off mid-sentence - almost always the 30s function limit. Say so in the
      // answer itself: silently truncated prose reads as a complete answer and
      // is more misleading than an error would be.
      if (matchedSSE && !sawTerminal) {
        console.warn('⚠️ Stream ended without a terminal event - answer may be truncated');
        if (fullContent) {
          fullContent +=
            '\n\n_This answer was cut short before it finished. Try asking a ' +
            'narrower question._';
        }
      }

      // Hitting the token cap ends the answer mid-sentence but still arrives as
      // a proper terminal event, so the cut-off check above does not catch it.
      // Say so rather than leaving a sentence hanging with no explanation.
      if (hitTokenCap && fullContent) {
        fullContent +=
          '\n\n_This answer reached its length limit and stops here. Ask a ' +
          'narrower question for a complete answer._';
      }

      // Check if we got no content at all
      if (!fullContent) {
        console.warn('⚠️ No content received from API');
        fullContent =
          matchedSSE && !sawTerminal
            ? // The stream was alive but was cut off before the model finished
              // searching - almost always the 30s serverless function limit.
              'The search timed out before an answer came back. Please try again, or ask a more specific question.'
            : 'No content was received from the API. This might be due to a configuration issue.';
      }

      const { content, relatedQuestions } = splitRelatedQuestions(fullContent);

      return {
        content,
        citations,
        related_questions: relatedQuestions,
      };
    } catch (error) {
      console.error('❌ API call failed:', error);
      if (error.name === 'AbortError') throw error;
      throw new Error('Failed to fetch answer: ' + error.message);
    }
  }

  // Helper function to escape HTML
  function escapeHtml(text) {
    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return text.replace(/[&<>"']/g, m => map[m]);
  }

  // formatStreamingChunk has been moved to formatStreamingChunk.js

  // formatResponse has been moved to formatResponse.js

  function displayResult(data) {

    const { content, relatedQuestions, citations } = formatResponse(data);

    console.log('📄 Final formatted content preview:', content.substring(0, 500));
    console.log('📄 Checking for unformatted headers:', content.match(/^###\s+/gm) ? 'FOUND UNFORMATTED HEADERS!' : 'No unformatted headers');

    // Display main content
    resultContainer.innerHTML = content;

    // Add citations
    if (citations && citations.length > 0) {
      let citationsHTML = '<div class="citations"><h3>Sources</h3><ol>';
      citations.forEach((citation, index) => {
        citationsHTML += `<li id="citation-${index + 1}"><a href="${citation}" target="_blank" rel="noopener noreferrer">${citation}</a></li>`;
      });
      citationsHTML += '</ol></div>';
      resultContainer.innerHTML += citationsHTML;
    }

    // Image results were a Sonar-only feature (`return_images`). The Agent API
    // has no equivalent, so there is nothing to render here any more.
    imageContainer.innerHTML = '';

    // Render related questions
    if (relatedQuestions && relatedQuestions.length > 0) {
      console.log('❓ Displaying', relatedQuestions.length, 'related questions');
      let relatedHTML = '<div class="related-questions"><h3>Related Questions</h3><ul>';
      relatedQuestions.forEach(question => {
        relatedHTML += `<li><a href="#" class="related-question" data-question="${escapeHtml(
          question
        )}">${escapeHtml(question)}</a></li>`;
      });
      relatedHTML += '</ul></div>';
      resultContainer.innerHTML += relatedHTML;
    }
  }

  // Search button handler (with streaming)
  searchButton.addEventListener('click', async () => {
    let query = searchInput.value.trim();
    console.log('🔍 Search clicked, query is:', query);

    query = expandQuery(query);

    clearError();
    if (!query) return displayError('Please enter a query');

    // Predefined questions were answered at build time - render immediately
    // rather than spending ~28s regenerating the same answer.
    const precomputed = await getPrecomputedAnswer(query);
    if (precomputed) {
      console.log('⚡ Serving precomputed answer (no API call)');
      if (abortController) abortController.abort();
      displayResult(precomputed);
      history.push({ role: 'user', content: query });
      history.push({ role: 'assistant', content: precomputed.content || '' });
      return;
    }

    if (abortController) abortController.abort();
    abortController = new AbortController();

    // Prepare streaming container
    resultContainer.innerHTML =
      '<div class="loading-animation">Loading...</div><div id="streaming-result" class="streaming-result"></div>';
    const streamingEl = document.getElementById('streaming-result');

    const onChunk = fullText => {
      // Apply progressive formatting to the streaming content
      const formattedChunk = formatStreamingChunk(fullText);
      streamingEl.innerHTML = formattedChunk;
    };

    try {
      showLoadingTime(10);
      const data = await callPerplexityAPI(query, abortController.signal, onChunk);
      hideLoading();

      // After stream completes, render fully formatted content
      displayResult(data);

      // Update history for conversation context
      history.push({ role: 'user', content: query });
      history.push({ role: 'assistant', content: data.content || '' });
    } catch (error) {
      hideLoading();
      if (error.name !== 'AbortError') {
        console.error('❌ Search error:', error);
        displayError(error.message);
      }
    } finally {
      hideLoadingTime();
    }
  });

  // Enter key handler
  searchInput.addEventListener('keydown', function (event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      searchButton.click();
    }
  });

  // Related questions handler
  document.addEventListener('click', function (e) {
    const target = e.target.closest('a');
    if (target && target.classList.contains('related-question')) {
      e.preventDefault();
      const question = target.textContent.trim();
      console.log('🔵 Related question clicked:', question);
      searchInput.value = question;
      scrollInputIntoView();
      searchButton.click();
    }
  });

  // Predefined questions handler
  document.addEventListener('click', function (e) {
    const target = e.target.closest('a');
    if (target && target.classList.contains('predefined-question')) {
      e.preventDefault();
      const question = target.textContent.trim();
      console.log('🔵 Predefined question clicked:', question);
      searchInput.value = question;
      scrollInputIntoView();
      searchButton.click();
    }
  });

  // Fallback for table cells (if no links)
  document.querySelectorAll('table td').forEach(cell => {
    const text = cell.textContent.trim();
    if (text.endsWith('?') && !cell.querySelector('a')) {
      cell.style.cursor = 'pointer';
      cell.style.color = '#2980b9';
      cell.addEventListener('click', function () {
        console.log('🔵 Table question clicked:', text);
        searchInput.value = text;
        scrollInputIntoView();
        searchButton.click();
      });
    }
  });

  // Summary buttons (optional streaming)
  document.getElementById('short-summary')?.addEventListener('click', () =>
    handleSummary(50)
  );
  document.getElementById('medium-summary')?.addEventListener('click', () =>
    handleSummary(150)
  );
  document.getElementById('long-summary')?.addEventListener('click', () =>
    handleSummary(300)
  );

  async function handleSummary(wordCount) {
    const prompt = `Summarize the Telia Ericsson NorthStar 5G innovation network in ${wordCount} words. Focus on technical aspects.`;

    if (summaryAbortController) summaryAbortController.abort();
    summaryAbortController = new AbortController();

    clearError();

    try {
      const summaryContent = document.getElementById('summary-content');
      if (summaryContent) {
        summaryContent.innerHTML =
          '<div class="loading-animation">Loading...</div><div id="summary-stream" class="streaming-result"></div>';
        const summaryStreamEl = document.getElementById('summary-stream');

        const stripRefs = (text) => text.replace(/\[\d+\]/g, '');

        const onChunk = fullText => {
          // Apply progressive formatting to the summary streaming content
          const formattedChunk = formatStreamingChunk(stripRefs(fullText));
          summaryStreamEl.innerHTML = formattedChunk;
        };

        const data = await callPerplexityAPI(
          prompt,
          summaryAbortController.signal,
          onChunk
        );
        // Strip citation references and don't pass citations to formatResponse
        data.content = stripRefs(data.content || '');
        data.citations = [];
        const formatted = formatResponse(data);
        summaryContent.innerHTML = formatted.content || 'No summary available';
      }
    } catch (error) {
      if (error.name !== 'AbortError') displayError(error.message);
    }
  }
});
